import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { Modal, useToast } from '../../componentes/ui';
import { api } from '../../lib/api';

interface Situacao {
  estado: 'desligado' | 'conectando' | 'aguardando_qr' | 'conectado';
  qr: string | null;
  numero: string | null;
  erro: string | null;
}

const ROTULO: Record<Situacao['estado'], string> = {
  desligado: 'Desconectado',
  conectando: 'Conectando…',
  aguardando_qr: 'Aguardando o QR',
  conectado: 'Conectado',
};

const formatar = (n: string) => n.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3');

export function RoboFotos() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState(false);
  const situacao = useQuery({
    queryKey: ['robo-fotos'],
    queryFn: () => api<Situacao>('/api/robo-fotos'),
    refetchInterval: (q) => (q.state.data?.estado === 'conectado' ? 30_000 : 2_500),
  });

  async function acao(caminho: 'conectar' | 'desconectar') {
    try {
      qc.setQueryData(
        ['robo-fotos'],
        await api<Situacao>(`/api/robo-fotos/${caminho}`, { metodo: 'POST' }),
      );
      if (caminho === 'desconectar') toast('Robô desconectado');
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setConfirmar(false);
    }
  }

  const s = situacao.data;
  return (
    <div className="config-cartao largo">
      <header>
        <h2>Robô de fotos</h2>
        <p>
          Busca a foto de perfil de quem manda mensagem pra loja, pra aparecer no lugar das
          iniciais. Ele só lê a foto: não envia, não marca como lida e não aparece online.
        </p>
      </header>

      {!s ? (
        <p className="nota-sec">Carregando…</p>
      ) : (
        <div className="robo">
          <div className="robo-estado">
            <span className={`robo-ponto ${s.estado}`} />
            <b>{ROTULO[s.estado]}</b>
            {s.numero && <span className="nota-sec">{formatar(s.numero)}</span>}
          </div>
          {s.erro && <p className="erro-form">{s.erro}</p>}

          {s.estado === 'aguardando_qr' && s.qr && (
            <div className="robo-qr">
              <img src={s.qr} alt="QR code pra conectar o robô" width={240} height={240} />
              <ol>
                <li>Abra o WhatsApp da loja no celular.</li>
                <li>
                  Toque em <b>⋮ → Aparelhos conectados → Conectar um aparelho</b>.
                </li>
                <li>Aponte a câmera pra este QR.</li>
              </ol>
            </div>
          )}

          <div className="robo-acoes">
            {s.estado === 'desligado' ? (
              <button className="btn primario" onClick={() => void acao('conectar')}>
                <Power /> Conectar
              </button>
            ) : (
              <button className="btn" onClick={() => setConfirmar(true)}>
                <PowerOff /> Desconectar
              </button>
            )}
          </div>

          <div className="robo-dicas">
            <Camera />
            <ul>
              <li>
                Busca só a foto de quem mandou mensagem, uma vez por semana no máximo, com intervalo
                entre uma e outra.
              </li>
              <li>
                Quem esconde a foto (só pra contatos que não salvaram a Viniko, ou pra ninguém)
                continua com as iniciais.
              </li>
              <li>
                Abra o WhatsApp no celular da loja pelo menos a cada 14 dias, senão o WhatsApp
                desconecta os aparelhos.
              </li>
            </ul>
          </div>
        </div>
      )}

      {confirmar && (
        <Modal titulo="Desconectar o robô" aoFechar={() => setConfirmar(false)}>
          <p style={{ margin: 0 }}>
            O robô sai dos aparelhos conectados do WhatsApp. As fotos que já foram buscadas
            continuam.
          </p>
          <div className="acoes">
            <button className="btn" onClick={() => setConfirmar(false)}>
              Cancelar
            </button>
            <button className="btn perigo-cheio" onClick={() => void acao('desconectar')}>
              Desconectar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
